import { FC, memo, useRef, useState } from "react";
import bytesToDisplayHelper from "utils/bytesToDisplayHelper";
import useMediaQueries from "hooks/useMediaQueries";
import useInboxQueryParams from "../../../../../hooks/useInboxQueryParams";
import Tooltip from "../../../../Elements/Tooltip/Tooltip";
import OutboxIcon from "./components/OutboxIcon";
import InboxIcon from "./components/InboxIcon";
import hasAdminAccessRights from "../../../../../helpers/hasAdminAccessRights";
import { useUser } from "contexts/userContext/userContext";
import { createPortal } from "react-dom";
import { useTransofrmedDate } from "utils/dateViewTransformer";
import { useContent } from "contexts/contentContext/contentContext";

//@ts-ignore
import styles from "./styles.module.scss";

interface RecipientInfoI {
  fullName: string;
  senderEmail: string;
  profilePhotoUrl: string;
}

interface SenderInfoI {
  fullName: string;
  deletedUserFullName: string;
  senderEmail: string;
  relatedTo: "subUser" | "currentUser";
  profilePhotoUrl: string;
  creationDate: string;
  filesQuantity: number;
  totalSize: number;
  recipients: string[];
  recipientInfo: RecipientInfoI;
  isCompactView: boolean;
  isOutbox: boolean;
}

const SenderInfo: FC<SenderInfoI> = memo((props) => {
  const {
    fullName,
    deletedUserFullName,
    relatedTo,
    senderEmail,
    profilePhotoUrl,
    creationDate,
    filesQuantity,
    totalSize,
    recipients,
    recipientInfo,
    isCompactView,
    isOutbox,
  } = props;

  const { mobileCondition, tabletCondition } = useMediaQueries();
  const { getQueryParam } = useInboxQueryParams();
  const maxSize = mobileCondition || tabletCondition ? 1 : 2;
  const { returnDate, returnTime } = useTransofrmedDate({ date: creationDate });
  const size = bytesToDisplayHelper(totalSize);
  const { getTextByKey } = useContent();
  const { getUserData } = useUser();
  const [showMoreRecipients, setShowMoreRecipients] = useState(false);
  const sentBySubUser = isOutbox && getUserData()?.UserName !== senderEmail;
  const showDeletedBy =
    getUserData()?.isTopUser || !!getUserData()?.subUserRole;
  const loggedUserFullName = `${getUserData()?.UserFirstName} ${
    getUserData()?.UserLastName
  }`;

  // It means if mail was sent by current logged user, who is organization's top/supervisor/admin user
  const sentByMe =
    isOutbox &&
    hasAdminAccessRights(
      getUserData()?.isTopUser,
      getUserData()?.subUserRole
    ) &&
    getUserData()?.UserName === senderEmail;

  const plusButtonRef = useRef<HTMLSpanElement>(null);

  const toggleShowMoreRecipients = () => setShowMoreRecipients((prev) => !prev);

  return (
    <div className={styles.wrapper}>
      {!recipients?.length || !isOutbox ? (
        <InboxIcon
          profilePhotoUrl={profilePhotoUrl}
          fullName={fullName}
          senderEmail={senderEmail}
        />
      ) : (
        <OutboxIcon
          isGroup={recipients?.length > 1}
          profilePhotoUrl={recipientInfo?.profilePhotoUrl}
          fullName={recipientInfo?.fullName}
          senderEmail={recipientInfo?.senderEmail}
          firstRecipient={recipients?.[0]}
        />
      )}
      <div className={styles.mailData}>
        <div className={styles.senderData}>
          {!recipients?.length ? (
            <span>{getTextByKey("mail-uploaded-as-link-text")}</span>
          ) : !isOutbox ? (
            <span className={styles.fullName}>{fullName}</span>
          ) : !isCompactView ? (
            <span>
              <span className={styles.sentTo}>
                {getTextByKey("outbox-to")}:
              </span>
              {recipients?.length === 1 && (
                <span className={styles.fullName}>
                  {" "}
                  {recipientInfo?.fullName}
                </span>
              )}
            </span>
          ) : null}
          {isOutbox ? (
            <span
              onMouseLeave={() => setShowMoreRecipients(false)}
              className={styles.recipientList}
            >
              <span className={styles.email}>
                {recipients?.slice(0, maxSize)?.join(", ")}
              </span>
              {recipients?.length > maxSize ? (
                <span className={styles.moreRecipients}>
                  <span onClick={toggleShowMoreRecipients} ref={plusButtonRef}>
                    + {recipients?.length - maxSize}
                    {showMoreRecipients &&
                      createPortal(
                        <span
                          className="more-recipients-popup-wrapper"
                          style={{
                            top:
                              plusButtonRef.current?.getBoundingClientRect()
                                ?.top! - 200,
                            left: mobileCondition
                              ? "unset"
                              : plusButtonRef.current?.getBoundingClientRect()
                                  ?.left! -
                                plusButtonRef.current?.getBoundingClientRect()
                                  ?.y!,
                          }}
                        >
                          <span className="more-recipients-popup">
                            <span>{getTextByKey("outbox-to")}:</span>
                            <span className={styles.moreRecipientList}>
                              {recipients?.join(", ")}
                            </span>
                          </span>
                        </span>,
                        document.getElementById("inbox-mails-container")!
                      )}
                  </span>
                </span>
              ) : (
                ""
              )}
            </span>
          ) : (
            <span className={styles.email}>{senderEmail}</span>
          )}
        </div>
        {sentBySubUser && !sentByMe && getQueryParam("t") !== "trash" && (
          <span className={styles.subUserEmail}>
            {/*@ts-ignore*/}
            <Tooltip tooltipText={senderEmail}>
              <span>{getTextByKey("outbox-sent-by")}: </span>
              <span>{fullName?.trim() || senderEmail}</span>
            </Tooltip>
          </span>
        )}
        {sentByMe && !sentBySubUser && getQueryParam("t") !== "trash" && (
          <span className={styles.subUserEmail}>
            {/*@ts-ignore*/}
            <Tooltip tooltipText={senderEmail}>
              <span>{getTextByKey("outbox-sent-by-me")}</span>
            </Tooltip>
          </span>
        )}
        {getQueryParam("t") === "trash" && showDeletedBy ? (
          <span className={styles.subUserEmail}>
            {deletedUserFullName === loggedUserFullName ? ( // TODO: full name should be current logged user name
              <span>{getTextByKey("trash-deleted-by-me")}</span>
            ) : (
              <>
                <span>{getTextByKey("trash-deleted-by")}: </span>
                <span>{deletedUserFullName}</span>
              </>
            )}
          </span>
        ) : null}
        <div className={styles.fileData}>
          <span>
            {returnDate} {returnTime}
          </span>
          {/* {!isCompactView && (
            <>
              <span className={styles.separator}>|</span>
              <span>
                {filesQuantity} {getTextByKey("files")}
              </span>
              <span className={styles.separator}>|</span>
              <span>{size.size + (size.type + "")}</span>
            </>
          )} */}
        </div>
      </div>
    </div>
  );
});

export default SenderInfo;
